// Direction B "Ink Header" cancellation card. Per spec §3.5 + §7.11 + 13-bis:
// rendered when cancel_appointment reaches `output-available`. Third (and last)
// of 3 moment-of-truth cards.
//
// Visual differentiation per design handoff: brand-pink eyebrow + cancel icon
// (NOT green check), single row = Status. Foot is neutral — "Want to pick a
// different date? Just let me know." — no theatrics, just an offer to rebook.
//
// Per spec §7.19: original_datetime renders in vendor_timezone, NOT the
// source ISO offset or the visitor's browser TZ. formatDatetime is the
// single source of truth.
//
// `original_datetime` is sourced from part.output. The shared cancel_appointment
// schema does not carry datetime fields — chat-only enrichment in
// chat_pipeline._enrich_chat_card_output injects `original_datetime` into
// the tool result from envelope.booking_data so this card can render the
// title without polluting the email/SMS tool schema.

import { useVendorInfo } from '../../context/VendorInfoContext';
import { parseToolOutput } from '../../lib/parse-tool-output';
import { formatDatetime } from '../../lib/format-datetime';
import { bookingStatusLabel } from '../../lib/booking-labels';
import { ActionCard } from './ActionCard';

export interface CancellationCardProps {
  input: Record<string, unknown>;
  output: unknown;
}

export function CancellationCard({
  input: _input,
  output,
}: CancellationCardProps) {
  const vendor = useVendorInfo();
  const parsed = parseToolOutput(output) ?? {};
  // RCA Change 1: render off the RESULT. cancel_appointment returns
  // {status:'cancelled'} on success, {status:'error'} on failure. Allowlist the
  // success status so malformed/empty output also renders nothing (never wrongly
  // tell the customer their tour was cancelled).
  if (parsed.status !== 'cancelled') return null;
  const orig = (parsed.original_datetime as string) || '';
  const formatted = formatDatetime(orig, vendor.vendor_timezone);

  // Title prefers the cancelled tour's datetime — that's the most-relevant
  // info (visually echoes what the customer is losing). Fallback to the vendor
  // booking-noun label ("Tour cancelled" for legacy) if datetime is missing.
  const label = bookingStatusLabel(vendor.booking_noun, 'cancelled');
  const title = formatted ? formatted : label;

  return (
    <ActionCard
      testId="cancellation-card"
      status="danger"
      eyebrow={label}
      title={title}
      rows={[{ label: 'Status', text: 'Cancelled' }]}
      foot={<>Want to pick a different date? Just let me know.</>}
    />
  );
}
